/**
 * Map Service
 * Handles map visualization, routing, and geocoding
 * Works in simulation mode without Mapbox/Google Maps API configured
 */

const MAP_CONFIG_KEY = 'hmgc_map_config';

// Default configuration
const defaultConfig = {
  provider: 'simulation', // 'simulation', 'mapbox', 'google_maps'
  apiKey: '',
  defaultCenter: { lat: 31.9686, lng: -99.9018 }, // Center of Texas
  defaultZoom: 6,
  showTraffic: false,
  showSatellite: false,
};

/**
 * Get map configuration from localStorage
 */
export const getMapConfig = () => {
  try {
    const config = localStorage.getItem(MAP_CONFIG_KEY);
    return config ? JSON.parse(config) : defaultConfig;
  } catch (error) {
    console.error('Error reading map config:', error);
    return defaultConfig;
  }
};

/**
 * Save map configuration to localStorage
 */
export const saveMapConfig = (config) => {
  try {
    localStorage.setItem(MAP_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (error) {
    console.error('Error saving map config:', error);
    return false;
  }
};

/**
 * Check if map API is configured (non-simulation mode)
 */
export const isMapConfigured = () => {
  const config = getMapConfig();
  return config.provider !== 'simulation' && config.apiKey;
};

/**
 * Geocode an address to coordinates (simulation mode)
 * In production, this would call Mapbox/Google Geocoding API
 */
export const geocodeAddress = async (address) => {
  const config = getMapConfig();
  
  if (config.provider === 'simulation' || !config.apiKey) {
    // Simulate geocoding with predefined locations
    return simulateGeocode(address);
  }

  // Real API call would go here
  // For now, fall back to simulation
  return simulateGeocode(address);
};

/**
 * Simulate geocoding based on address keywords
 */
const simulateGeocode = (address) => {
  const addressLower = address.toLowerCase();
  
  // Common Texas cities
  const cityCoordinates = {
    'houston': { lat: 29.7604, lng: -95.3698 },
    'dallas': { lat: 32.7767, lng: -96.7970 },
    'san antonio': { lat: 29.4241, lng: -98.4936 },
    'austin': { lat: 30.2672, lng: -97.7431 },
    'fort worth': { lat: 32.7555, lng: -97.3308 },
    'el paso': { lat: 31.7619, lng: -106.4850 },
    'arlington': { lat: 32.7357, lng: -97.1081 },
    'corpus christi': { lat: 27.8006, lng: -97.3964 },
    'plano': { lat: 33.0198, lng: -96.6989 },
    'laredo': { lat: 27.5306, lng: -99.4803 },
    'lubbock': { lat: 33.5779, lng: -101.8552 },
    'garland': { lat: 32.9126, lng: -96.6389 },
    'irving': { lat: 32.8140, lng: -96.9489 },
    'amarillo': { lat: 35.2220, lng: -101.8313 },
    'grand prairie': { lat: 32.7460, lng: -96.9978 },
    'brownsville': { lat: 25.9017, lng: -97.4975 },
    'mckinney': { lat: 33.1972, lng: -96.6397 },
    'frisco': { lat: 33.1507, lng: -96.8236 },
    'pasadena': { lat: 29.6911, lng: -95.2091 },
    'mesquite': { lat: 32.7668, lng: -96.5992 },
    // Other states
    'phoenix': { lat: 33.4484, lng: -112.0740 },
    'albuquerque': { lat: 35.0844, lng: -106.6504 },
    'las vegas': { lat: 36.1627, lng: -115.1391 },
    'denver': { lat: 39.7392, lng: -104.9903 },
    'oklahoma city': { lat: 35.4676, lng: -97.5164 },
    'tulsa': { lat: 36.1540, lng: -95.9928 },
    'new orleans': { lat: 29.9511, lng: -90.0715 },
    'memphis': { lat: 35.1495, lng: -90.0490 },
  };

  // Check for city matches
  for (const [city, coords] of Object.entries(cityCoordinates)) {
    if (addressLower.includes(city)) {
      // Add some randomness to simulate different addresses in the same city
      return {
        lat: coords.lat + (Math.random() - 0.5) * 0.05,
        lng: coords.lng + (Math.random() - 0.5) * 0.05,
        formattedAddress: address,
        confidence: 0.85,
      };
    }
  }

  // Default to random Texas location if no match
  return {
    lat: 31.9686 + (Math.random() - 0.5) * 4,
    lng: -99.9018 + (Math.random() - 0.5) * 6,
    formattedAddress: address,
    confidence: 0.5,
  };
};

/**
 * Get route between two points (simulation mode)
 * In production, this would call Mapbox/Google Directions API
 */
export const getRoute = async (origin, destination) => {
  const config = getMapConfig();
  
  if (config.provider === 'simulation' || !config.apiKey) {
    return simulateRoute(origin, destination);
  }

  // Real API call would go here
  return simulateRoute(origin, destination);
};

/**
 * Simulate route calculation
 */
const simulateRoute = (origin, destination) => {
  // Calculate straight-line distance
  const R = 6371; // Earth's radius in km
  const dLat = (destination.lat - origin.lat) * Math.PI / 180;
  const dLng = (destination.lng - origin.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(origin.lat * Math.PI / 180) * Math.cos(destination.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const straightLineDistance = R * c;

  // Estimate road distance (typically 1.3-1.5x straight line)
  const roadDistance = straightLineDistance * 1.4;
  
  // Estimate duration (average 80 km/h)
  const durationHours = roadDistance / 80;
  const durationMinutes = Math.round(durationHours * 60);

  // Generate waypoints for the route (simplified)
  const waypoints = generateWaypoints(origin, destination, 10);

  return {
    distance: {
      value: Math.round(roadDistance * 1000), // meters
      text: `${Math.round(roadDistance)} km (${Math.round(roadDistance * 0.621371)} mi)`,
    },
    duration: {
      value: durationMinutes * 60, // seconds
      text: formatDuration(durationMinutes),
    },
    waypoints,
    polyline: waypoints, // In real implementation, this would be encoded polyline
  };
};

/**
 * Generate waypoints between two points
 */
const generateWaypoints = (origin, destination, numPoints) => {
  const waypoints = [];
  
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    // Add some curve to make it look more like a road
    const curve = Math.sin(t * Math.PI) * 0.02;
    
    waypoints.push({
      lat: origin.lat + (destination.lat - origin.lat) * t + curve,
      lng: origin.lng + (destination.lng - origin.lng) * t,
    });
  }
  
  return waypoints;
};

/**
 * Format duration in human-readable format
 */
const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${mins} min`;
};

/**
 * Get route with multiple stops
 */
export const getMultiStopRoute = async (stops) => {
  if (stops.length < 2) {
    return null;
  }

  const routes = [];
  let totalDistance = 0;
  let totalDuration = 0;
  const allWaypoints = [];

  for (let i = 0; i < stops.length - 1; i++) {
    const route = await getRoute(stops[i], stops[i + 1]);
    routes.push(route);
    totalDistance += route.distance.value;
    totalDuration += route.duration.value;
    allWaypoints.push(...route.waypoints);
  }

  return {
    legs: routes,
    totalDistance: {
      value: totalDistance,
      text: `${Math.round(totalDistance / 1000)} km (${Math.round(totalDistance / 1609.34)} mi)`,
    },
    totalDuration: {
      value: totalDuration,
      text: formatDuration(Math.round(totalDuration / 60)),
    },
    waypoints: allWaypoints,
  };
};

/**
 * Calculate bounds for a set of coordinates
 */
export const calculateBounds = (coordinates) => {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  coordinates.forEach(coord => {
    minLat = Math.min(minLat, coord.lat);
    maxLat = Math.max(maxLat, coord.lat);
    minLng = Math.min(minLng, coord.lng);
    maxLng = Math.max(maxLng, coord.lng);
  });

  return {
    southwest: { lat: minLat, lng: minLng },
    northeast: { lat: maxLat, lng: maxLng },
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    },
  };
};

/**
 * Get appropriate zoom level for bounds
 */
export const getZoomForBounds = (bounds, mapWidth = 800, mapHeight = 600) => {
  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 18;

  const latRad = (lat) => {
    const sin = Math.sin(lat * Math.PI / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const zoom = (mapPx, worldPx, fraction) => {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  };

  const latFraction = (latRad(bounds.northeast.lat) - latRad(bounds.southwest.lat)) / Math.PI;
  const lngDiff = bounds.northeast.lng - bounds.southwest.lng;
  const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

  const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
};

/**
 * Get marker icon based on type
 */
export const getMarkerIcon = (type) => {
  const icons = {
    truck: '🚛',
    pickup: '📦',
    delivery: '🏠',
    warehouse: '🏭',
    customer: '🏢',
    driver: '👤',
  };
  return icons[type] || '📍';
};

/**
 * Get marker color based on status
 */
export const getMarkerColor = (status) => {
  const colors = {
    active: '#16c79a',
    pending: '#f39c12',
    completed: '#3498db',
    delayed: '#e74c3c',
    en_route: '#9b59b6',
  };
  return colors[status] || '#7f8c8d';
};

/**
 * Format coordinates for display
 */
export const formatCoordinates = (lat, lng, format = 'decimal') => {
  if (format === 'dms') {
    // Degrees, Minutes, Seconds format
    const toDMS = (coord, isLat) => {
      const absolute = Math.abs(coord);
      const degrees = Math.floor(absolute);
      const minutesNotTruncated = (absolute - degrees) * 60;
      const minutes = Math.floor(minutesNotTruncated);
      const seconds = Math.floor((minutesNotTruncated - minutes) * 60);
      const direction = isLat 
        ? (coord >= 0 ? 'N' : 'S')
        : (coord >= 0 ? 'E' : 'W');
      return `${degrees}°${minutes}'${seconds}"${direction}`;
    };
    return `${toDMS(lat, true)} ${toDMS(lng, false)}`;
  }
  
  // Decimal format
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
};

/**
 * Create static map URL (for preview without interactive map)
 */
export const getStaticMapUrl = (center, zoom = 12, width = 400, height = 300, markers = []) => {
  const config = getMapConfig();
  
  if (config.provider === 'mapbox' && config.apiKey) {
    // Mapbox Static Images API
    const markerStr = markers.map(m => `pin-s+${m.color || 'ff0000'}(${m.lng},${m.lat})`).join(',');
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${markerStr}/${center.lng},${center.lat},${zoom}/${width}x${height}?access_token=${config.apiKey}`;
  }
  
  if (config.provider === 'google_maps' && config.apiKey) {
    // Google Static Maps API
    const markerStr = markers.map(m => `markers=color:${m.color || 'red'}|${m.lat},${m.lng}`).join('&');
    return `https://maps.googleapis.com/maps/api/staticmap?center=${center.lat},${center.lng}&zoom=${zoom}&size=${width}x${height}&${markerStr}&key=${config.apiKey}`;
  }
  
  // Return placeholder for simulation mode
  return null;
};

let googleMapsLoadPromise = null;

/**
 * Load Google Maps JavaScript API dynamically
 * Returns a promise that resolves when the API is loaded
 */
export const loadGoogleMapsScript = (apiKey) => {
  if (window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById('google-maps-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google.maps));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-script';
    // __BOARD_MAPS_KEY__ places: fuel-stop search runs through the JS API (the REST
    // Places/Geocoding endpoints refuse a website-restricted browser key).
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=geometry,places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps failed to initialize'));
      }
    };

    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error('Failed to load Google Maps script. Please check your API key and ensure Maps JavaScript API is enabled.'));
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

/**
 * Check if Google Maps JS API is loaded
 */
export const isGoogleMapsLoaded = () => {
  return !!(window.google && window.google.maps);
};

/**
 * Decode an encoded polyline string into an array of lat/lng points
 * This is the standard Google polyline encoding algorithm
 */
export const decodePolyline = (encoded) => {
  if (!encoded) return [];
  
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let shift = 0;
    let result = 0;
    let byte;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlat = (result & 1) ? ~(result >> 1) : (result >> 1);
    lat += dlat;

    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const dlng = (result & 1) ? ~(result >> 1) : (result >> 1);
    lng += dlng;

    points.push({
      lat: lat / 1e5,
      lng: lng / 1e5
    });
  }

  return points;
};

const mapService = {
  getMapConfig,
  saveMapConfig,
  isMapConfigured,
  geocodeAddress,
  getRoute,
  getMultiStopRoute,
  calculateBounds,
  getZoomForBounds,
  getMarkerIcon,
  getMarkerColor,
  formatCoordinates,
  getStaticMapUrl,
  loadGoogleMapsScript,
  isGoogleMapsLoaded,
  decodePolyline,
};

export default mapService;
